import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// Change item component for individual changelog entries
const ChangeItem = ({ change }) => {
  const { t } = useTranslation();
  const getBadgeForCategory = (text) => {
    const categories = {
      '[FEATURE]': { label: t('Feature'), color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
      '[CLIENT]': { label: t('Client'), color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      '[ADMIN]': { label: t('Admin'), color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
      '[IMPROVEMENT]': { label: t('Improvement'), color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      '[DEBUG]': { label: t('Debug'), color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' }
    };

    for (const [code, badge] of Object.entries(categories)) {
      if (text.startsWith(code)) {
        return {
          badge,
          text: text.replace(code, '').trim()
        };
      }
    }
    return { text };
  };

  const { badge, text } = getBadgeForCategory(change);

  return (
    <li className="text-gray-600 dark:text-gray-300 text-sm flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
      {badge && (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit ${badge.color}`}>
          {badge.label}
        </span>
      )}
      <span>{text}</span>
    </li>
  );
};

// Release entry component for a version release
const ReleaseEntry = ({ release, isLast }) => {
  return (
    <div>
      <div className="mb-6 md:grid md:grid-cols-12 md:gap-6">
        {/* Version et date - Colonne de gauche sur desktop */}
        <div className="mb-3 md:mb-0 md:col-span-3">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="font-medium text-gray-800 dark:text-gray-200">
              v{release.version}
            </span>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {dayjs(release.date).format('D MMMM YYYY')}
          </div>
        </div>

        {/* Liste des changements - Colonne de droite sur desktop */}
        <div className="md:col-span-9">
          <ul className="space-y-3">
            {release.changes.map((change, changeIndex) => (
              <ChangeItem key={changeIndex} change={change} />
            ))}
          </ul>
        </div>
      </div>

      {/* Séparateur (sauf pour le dernier élément) */}
      {!isLast && (
        <div className="border-t border-dotted border-gray-200 dark:border-gray-400 my-6 w-[75px] mx-auto"></div>
      )}
    </div>
  );
};

// Main Changelog component
function Changelog() {
  const { t } = useTranslation();
  const [changelog, setChangelog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/changelog.json')
      .then(response => response.json())
      .then(data => {
        setChangelog(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Erreur lors du chargement du changelog:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="w-100 text-center text-gray-600 dark:text-gray-300">
          {t('Loading updates...')}
        </div>
      </div>
    );
  }

  if (!changelog || !Array.isArray(changelog) || changelog.length === 0)
    return null;

  const sortedChangelog = [...changelog].reverse();

  return (
    <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('Latest updates')}
            </h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
              {t('Admin')}
            </span>
          </div>
        </div>
        <div className="border-b border-gray-200 dark:border-gray-700"></div>
      </div>

      {sortedChangelog.map((release, index) => (
        <ReleaseEntry
          key={release.version}
          release={release}
          isLast={index === sortedChangelog.length - 1}
        />
      ))}
    </div>
  );
}

export default Changelog;